import styled, { keyframes, css } from 'styled-components';
import { 
  LayoutType, 
  GridConfig, 
  FlexConfig,
  FadeAnimationType 
} from './FadeInContainer.types';

// Animaciones fade-in
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 100%, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, -100%, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translate3d(-100%, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translate3d(100%, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

// Función para obtener la animación según el tipo
const getAnimation = (animationType: FadeAnimationType) => {
  switch (animationType) {
    case 'fade':
      return fadeIn;
    case 'fadeUp':
      return fadeInUp;
    case 'fadeDown':
      return fadeInDown;
    case 'fadeLeft':
      return fadeInLeft;
    case 'fadeRight':
      return fadeInRight;
    case 'none':
      return null;
    default:
      return fadeInUp;
  }
};

// Props para el contenedor genérico
interface ContainerProps {
  layout: LayoutType;
  gap: string;
  gridConfig?: GridConfig;
  flexConfig?: FlexConfig;
}

// Estilos para layout grid
const gridStyles = css<ContainerProps>`
  display: grid;
  grid-template-columns: ${(props) => {
    if (props.gridConfig?.templateColumns) {
      return props.gridConfig.templateColumns;
    }
    if (props.gridConfig?.columns) {
      return `repeat(${props.gridConfig.columns}, 1fr)`;
    }
    return 'repeat(3, 1fr)'; // default
  }};
  
  ${(props) => props.gridConfig?.templateRows && css`
    grid-template-rows: ${props.gridConfig.templateRows};
  `}
  
  ${(props) => props.gridConfig?.rows && !props.gridConfig?.templateRows && css`
    grid-template-rows: repeat(${props.gridConfig.rows}, 1fr);
  `}

  ${(props) => props.gridConfig?.templateAreas && css`
    grid-template-areas: ${props.gridConfig.templateAreas};
  `}

  /* Responsividad para tablets */
  @media (max-width: 768px) {
    ${(props) => {
      if (props.gridConfig?.tablet) {
        const tabletConfig = props.gridConfig.tablet;
        return css`
          ${tabletConfig.templateColumns && css`
            grid-template-columns: ${tabletConfig.templateColumns};
          `}
          ${tabletConfig.columns && !tabletConfig.templateColumns && css`
            grid-template-columns: repeat(${tabletConfig.columns}, 1fr);
          `}
          ${tabletConfig.templateRows && css`
            grid-template-rows: ${tabletConfig.templateRows};
          `}
          ${tabletConfig.templateAreas && css`
            grid-template-areas: ${tabletConfig.templateAreas};
          `}
        `;
      }
      // Solo aplicar default si no hay configuración específica
      return css`
        grid-template-columns: repeat(2, 1fr);
      `;
    }}
  }

  /* Responsividad para móviles */
  @media (max-width: 480px) {
    ${(props) => {
      if (props.gridConfig?.mobile) {
        const mobileConfig = props.gridConfig.mobile;
        return css`
          ${mobileConfig.templateColumns && css`
            grid-template-columns: ${mobileConfig.templateColumns};
          `}
          ${mobileConfig.columns && !mobileConfig.templateColumns && css`
            grid-template-columns: repeat(${mobileConfig.columns}, 1fr);
          `}
          ${mobileConfig.templateRows && css`
            grid-template-rows: ${mobileConfig.templateRows};
          `}
          ${mobileConfig.templateAreas && css`
            grid-template-areas: ${mobileConfig.templateAreas};
          `}
        `;
      }
      return css`
        grid-template-columns: 1fr;
      `;
    }}
  }
`;

// Función para estilos flex
const getFlexStyles = (props: ContainerProps) => css`
  flex-direction: ${props.flexConfig?.direction || 'row'};
  justify-content: ${props.flexConfig?.justifyContent || 'flex-start'};
  align-items: ${props.flexConfig?.alignItems || 'stretch'};
  flex-wrap: ${props.flexConfig?.wrap ? 'wrap' : 'nowrap'};
`;

// Estilos para otros tipos de layout
const getLayoutStyles = (layout: LayoutType) => {
  switch (layout) {
    case 'block':
      return css`display: block;`;
    case 'inline-flex':
      return css`display: inline-flex;`;
    case 'inline-block':
      return css`display: inline-block;`;
    default:
      return css`display: block;`;
  }
};

// Container principal genérico
export const Container = styled.div<ContainerProps>`
  width: 100%;
  gap: ${(props) => props.gap};

  ${(props) => {
    switch (props.layout) {
      case 'grid':
        return gridStyles;
      case 'flex':
        return css`
          display: flex;
          ${getFlexStyles(props)}
        `;
      case 'inline-flex':
        return css`
          display: inline-flex;
          ${getFlexStyles(props)}
        `;
      default:
        return getLayoutStyles(props.layout);
    }
  }}
`;

// Wrapper para cada elemento hijo con animación
export const AnimatedItem = styled.div<{
  animationDuration: string;
  delay: number;
  preserveLayout?: boolean;
  animationType: FadeAnimationType;
}>`
  ${(props) => props.preserveLayout && css`
    display: contents;
  `}
  
  ${(props) => {
    const animation = getAnimation(props.animationType);
    
    if (props.animationType === 'none') {
      return css`
        opacity: 1;
      `;
    }
    
    if (!animation) {
      return css`
        opacity: 1;
      `;
    }
    
    return css`
      opacity: 0;
      animation: ${animation} ${props.animationDuration} ease-out forwards;
      animation-delay: ${props.delay}ms;
    `;
  }}

  ${(props) => props.preserveLayout && props.animationType !== 'none' && css`
    /* Cuando usamos display: contents, aplicamos la animación a los hijos */
    > * {
      ${() => {
        const animation = getAnimation(props.animationType);
        if (!animation) return '';
        
        return css`
          opacity: 0;
          animation: ${animation} ${props.animationDuration} ease-out forwards;
          animation-delay: ${props.delay}ms;
        `;
      }}
    }
    
    /* Resetear la animación del contenedor */
    opacity: 1;
    animation: none;
  `}
`;
